import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import AdminTabs from '@/features/admin/components/AdminTabs';
import Modal from '@/features/admin/components/AdminModal';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { safeErrorMessage } from '@/shared/utils/errors';
import { formatShortDate } from '@/shared/utils/format';
import { fetchHomeSections } from '@/features/browse/services/homeService';
import {
  createFeatured,
  deleteFeatured,
  listFeaturedAdmin,
  updateFeatured,
} from '@/features/admin/services/adminService';
import type { AdminFeaturedVideo, HomeSection } from '@/shared/types';

/**
 * Admin → Featured: what is on the Home page, and which rail each entry sits in.
 *
 * The only surface that can classify a video. Until this existed, curation was
 * `POST /admin/featured` by hand and re-curation was DELETE + POST — which mints
 * a new curation-row id and silently orphans every user's save, note, favourite
 * and resume point for that video (they key on that id, with no foreign key
 * behind them). Everything here edits in place.
 */

const UNCATEGORISED = '';

/** Shown instead of an empty cell, so a gap in the catalogue is something a
    curator reads rather than something they have to notice. This table is the
    only place an uncategorised entry is visible at all: Home just quietly leaves
    it out of the section rails. */
const NO_SECTION_LABEL = '— none —';

/** Comma-separated in and out. Tags are descriptive free text here — the rail is
    decided by `category` — so a plain text field is the honest control. */
function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

const featuredColumnHelper = createDataTableColumns<AdminFeaturedVideo>();

function buildColumns(
  sectionLabel: (slug: string | null) => string,
  onEdit: (row: AdminFeaturedVideo) => void,
  onRemove: (row: AdminFeaturedVideo) => void,
) {
  return featuredColumnHelper.columns([
    featuredColumnHelper.accessor('position', {
      header: 'Pos',
      filterFn: 'includesString',
    }),
    featuredColumnHelper.accessor('title', {
      header: 'Title',
      filterFn: 'includesString',
      meta: { align: 'left' },
    }),
    featuredColumnHelper.accessor((row) => sectionLabel(row.category), {
      id: 'category',
      header: 'Section',
      filterFn: 'includesString',
    }),
    featuredColumnHelper.accessor((row) => row.tags.join(', '), {
      id: 'tags',
      header: 'Tags',
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => info.getValue() || '—',
    }),
    featuredColumnHelper.accessor((row) => row.jobStatus ?? '—', {
      id: 'jobStatus',
      header: 'Job',
      filterFn: 'includesString',
      /* An entry whose job has failed or lost its video is listed here and
         hidden from Home, arriving with an empty videoUrl. Saying so in the row
         is the whole reason the admin list is a different endpoint. */
      cell: (info) => {
        const row = info.row.original;
        if (!row.videoUrl) return `${info.getValue()} · not playable`;
        return info.getValue();
      },
    }),
    featuredColumnHelper.accessor((row) => (row.showcase ? 'Public' : 'Signed-in only'), {
      id: 'showcase',
      header: 'Visibility',
      filterFn: 'includesString',
      /* Worth a column of its own rather than living only inside the edit modal:
         "which of these is on the internet" is a question a curator should be able
         to answer by looking at the list, not by opening every row. */
    }),
    featuredColumnHelper.accessor((row) => formatShortDate(row.publishedAt, '—'), {
      id: 'publishedAt',
      header: 'Featured',
      filterFn: 'includesString',
    }),
    featuredColumnHelper.display({
      id: 'actions',
      header: 'Actions',
      /* `.button secondary .admin-table-actions__compact`, which is the row-action
         pattern for a button with WORDS in it (AdminUserJobsPage's "Revert
         credits" is the other user of it). Not `.admin-action-btn` — despite the
         name, that one is a fixed 34x34 icon square, sized for the inline <svg>
         children it carries on the users page. "Edit" and "Remove" simply painted
         outside it. */
      cell: (info) => (
        <div className="admin-table-actions">
          <button
            type="button"
            className="button secondary admin-table-actions__compact"
            onClick={() => onEdit(info.row.original)}
          >
            Edit
          </button>
          <button
            type="button"
            className="button secondary admin-table-actions__compact"
            onClick={() => onRemove(info.row.original)}
          >
            Remove
          </button>
        </div>
      ),
    }),
  ]);
}

// --- The shared form body, used by both modals ---------------------------------

type FormState = {
  title: string;
  description: string;
  tags: string;
  category: string;
  position: string;
};

function FormFields({
  idPrefix,
  sections,
  value,
  onChange,
}: {
  idPrefix: string;
  sections: HomeSection[];
  value: FormState;
  onChange: (next: FormState) => void;
}) {
  return (
    <>
      <div className="form-field">
        <label className="field-label" htmlFor={`${idPrefix}-title`}>
          Title
        </label>
        <input
          id={`${idPrefix}-title`}
          type="text"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
        <p className="field-note">Leave empty to use the video&rsquo;s own generated title.</p>
      </div>

      <div className="form-field">
        <label className="field-label" htmlFor={`${idPrefix}-description`}>
          Description
        </label>
        <input
          id={`${idPrefix}-description`}
          type="text"
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
        />
      </div>

      <div className="form-field">
        <label className="field-label" htmlFor={`${idPrefix}-category`}>
          Section
        </label>
        {/* A native select, like the role picker on the users page. The menu
            component next door is for sort controls in the app chrome; every
            form field in this codebase uses a real select. */}
        <select
          id={`${idPrefix}-category`}
          value={value.category}
          onChange={(e) => onChange({ ...value, category: e.target.value })}
        >
          <option value={UNCATEGORISED}>{NO_SECTION_LABEL}</option>
          {sections.map((section) => (
            <option key={section.slug} value={section.slug}>
              {section.label}
            </option>
          ))}
          {/* A stored slug the catalogue no longer names needs an option of its
              own, or the control lies. A controlled <select> whose value matches
              no option renders the FIRST one — so the row read "recipes" in the
              table and "— none —" here, and the curator had no way to see what
              was actually stored, let alone that it was the reason their save
              was failing. Found on a local run. */}
          {value.category !== UNCATEGORISED &&
          !sections.some((section) => section.slug === value.category) ? (
            <option value={value.category}>{value.category} (unknown section)</option>
          ) : null}
        </select>
        <p className="field-note">
          Which Home rail this appears in. Uncategorised entries still show under Recently Added.
        </p>
      </div>

      <div className="form-field">
        <label className="field-label" htmlFor={`${idPrefix}-tags`}>
          Tags
        </label>
        <input
          id={`${idPrefix}-tags`}
          type="text"
          value={value.tags}
          onChange={(e) => onChange({ ...value, tags: e.target.value })}
        />
        <p className="field-note">
          Comma separated. Descriptive only &mdash; shown on the card, and never used to build a rail.
        </p>
      </div>

      <div className="form-field">
        <label className="field-label" htmlFor={`${idPrefix}-position`}>
          Position
        </label>
        <input
          id={`${idPrefix}-position`}
          type="number"
          value={value.position}
          onChange={(e) => onChange({ ...value, position: e.target.value })}
        />
        <p className="field-note">Lower sorts first, across the whole catalogue and inside every rail.</p>
      </div>
    </>
  );
}

// --- Edit ----------------------------------------------------------------------

function EditFeaturedModal({
  entry,
  sections,
  onClose,
  onSaved,
}: {
  entry: AdminFeaturedVideo;
  sections: HomeSection[];
  onClose: () => void;
  onSaved: (updated: AdminFeaturedVideo) => void;
}) {
  const initial: FormState = useMemo(
    () => ({
      title: entry.title,
      description: entry.description,
      tags: entry.tags.join(', '),
      category: entry.category ?? UNCATEGORISED,
      position: String(entry.position),
    }),
    [entry],
  );
  const [form, setForm] = useState<FormState>(initial);
  /* Held beside FormState rather than in it, deliberately. FormState is shared with
     the Add modal, and publishing to the public landing page should not be something
     a curator can do in the same gesture that features an entry -- the whole point of
     the separate flag is that the two decisions are made separately. So this control
     exists only here, on an entry that already exists. */
  const [showcase, setShowcase] = useState(entry.showcase);
  const [saving, setSaving] = useState(false);
  /* The guard has to be a ref, not the state beside it. `setSaving(true)` does
     not take effect until the next render, so two submits in the same tick both
     read `saving === false` and both go out — measured, two PATCHes for one
     save. A ref updates synchronously, which is the whole difference. `saving`
     stays for the button label and the disabled attribute, which are render
     concerns. */
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    /* Guards the handler, not just the button. Disabling the footer submit does
       not disable implicit submission — Enter in any text field still fires this
       — so a quick double-Enter sent two requests, and on the Add form the
       second came back as the 409 for a curation the first had just completed. */
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      /* Only what actually changed. The endpoint is a PATCH that reads
         `exclude_unset`, and sending all five fields every time threw that away
         — which broke a real case rather than merely wasting bytes: an entry
         holding a category the catalogue no longer names re-sent that slug on
         every save, so the engine 422'd and the row became uneditable. The
         curator could not fix its title, its tags, or the bad category itself.

         Diffing against the values the form opened with also means a field
         nobody touched can never be rewritten by a rounding or trimming
         difference between the two. */
      const changed: Parameters<typeof updateFeatured>[1] = {};
      /* An emptied box means "fall back to the video's own title", which is what
         the note under the field promises and what the Add form already does. It
         has to travel as `null`, not `''`: the engine resolves the fallback with
         `row.title or ai_title`, and an empty string is a value that overrides
         nothing — it would leave a card and a NOW SHOWING hero with no title at
         all, and because this page (rightly) refuses delete-and-recreate, no way
         back to the generated one. */
      if (form.title.trim() !== initial.title) changed.title = form.title.trim() || null;
      if (form.description.trim() !== initial.description) {
        changed.description = form.description.trim() || null;
      }
      if (form.tags !== initial.tags) changed.tags = parseTags(form.tags);
      if (form.category !== initial.category) {
        /* `null`, not `undefined`, when the curator picks the empty option.
           `undefined` is dropped by JSON.stringify and would read as "absent" on
           the far side, making "move this back to uncategorised" inexpressible
           from the one control that says it. */
        changed.category = form.category === UNCATEGORISED ? null : form.category;
      }
      /* Only a number the curator actually typed. `Number('') || 0` sent a 0 for
         an emptied box — and 0 is not a neutral value here, it is the front of
         the whole catalogue and of every rail the entry sits in. A `type=number`
         input also yields `''` for anything it refuses to parse, so the same
         silent jump followed a typo. An unusable value declines to send the
         field rather than guessing at one. */
      if (form.position !== initial.position) {
        const position = Number(form.position);
        if (form.position.trim() !== '' && Number.isFinite(position)) {
          changed.position = position;
        }
      }

      /* Diffed against the entry as it opened, like every field above, so a save
         that did not touch the checkbox does not re-assert its value. */
      if (showcase !== entry.showcase) changed.showcase = showcase;

      const updated = await updateFeatured(entry.id, changed);
      onSaved(updated);
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to save these changes.'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit featured entry"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-featured-form" className="button" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-featured-form" onSubmit={onSubmit}>
        <FormFields idPrefix="ef" sections={sections} value={form} onChange={setForm} />

        {/* Public visibility, kept visually apart from the curation fields above
            because it is a different kind of decision: everything else changes how
            an entry looks to signed-in users, this one changes who can see it at
            all. */}
        <div className="form-field">
          {/* `form-field` / `field-note` are the classes FormFields above uses, so
              this row sits in the same rhythm as the fields it follows. The
              label-wraps-input shape is PromptPage's `prompt-advanced__toggle`
              pattern, the app's one existing checkbox. */}
          <label className="admin-featured__check">
            <input
              type="checkbox"
              checked={showcase}
              onChange={(event) => setShowcase(event.target.checked)}
            />
            <span>Show on public landing page</span>
          </label>
          <p className="field-note">
            Anyone can watch this video without signing in. Featuring an entry does not
            publish it &mdash; only this does.
          </p>
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </form>
    </Modal>
  );
}

// --- Add -----------------------------------------------------------------------

function AddFeaturedModal({
  sections,
  onClose,
  onCreated,
}: {
  sections: HomeSection[];
  onClose: () => void;
  onCreated: (created: AdminFeaturedVideo) => void;
}) {
  const [jobId, setJobId] = useState('');
  const [form, setForm] = useState<FormState>({
    title: '',
    description: '',
    tags: '',
    category: UNCATEGORISED,
    position: '0',
  });
  const [saving, setSaving] = useState(false);
  /* The guard has to be a ref, not the state beside it. `setSaving(true)` does
     not take effect until the next render, so two submits in the same tick both
     read `saving === false` and both go out — measured, two PATCHes for one
     save. A ref updates synchronously, which is the whole difference. `saving`
     stays for the button label and the disabled attribute, which are render
     concerns. */
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    /* Guards the handler, not just the button. Disabling the footer submit does
       not disable implicit submission — Enter in any text field still fires this
       — so a quick double-Enter sent two requests, and on the Add form the
       second came back as the 409 for a curation the first had just completed. */
    if (savingRef.current) return;
    /* The same footgun the Edit form was fixed for, and it was still here:
       `Number(form.position) || 0` turned an emptied or unparseable box into a
       silent 0 — the front of the whole catalogue and of every rail — with
       nothing telling the curator their value had not been used. A `type=number`
       input yields `''` for anything it refuses to parse, so a typo landed there
       too.

       Edit's answer was to decline to send the field, which leaves the stored
       value alone. A create has no stored value to leave alone, so the honest
       answer is the other one: say so, and let them fix it. The box is
       pre-filled with `0`, so this only ever fires on a deliberate clear or a
       mistype. */
    const position = Number(form.position);
    if (form.position.trim() === '' || !Number.isFinite(position)) {
      setError('Position must be a whole number.');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const created = await createFeatured({
        jobId: jobId.trim(),
        title: form.title.trim() || undefined,
        description: form.description.trim() || undefined,
        tags: parseTags(form.tags),
        category: form.category === UNCATEGORISED ? null : form.category,
        position,
      });
      onCreated(created);
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to feature this job.'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Feature a job"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="add-featured-form" className="button" disabled={saving}>
            {saving ? 'Adding…' : 'Add to Home'}
          </button>
        </>
      }
    >
      <form id="add-featured-form" onSubmit={onSubmit}>
        <div className="form-field">
          <label className="field-label" htmlFor="af-job">
            Job ID
          </label>
          <input
            id="af-job"
            type="text"
            required
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
          <p className="field-note">
            The job must be completed and still have its video. Job IDs are listed under a user&rsquo;s videos.
          </p>
        </div>
        <FormFields idPrefix="af" sections={sections} value={form} onChange={setForm} />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </form>
    </Modal>
  );
}

// --- Page ----------------------------------------------------------------------

export default function AdminFeaturedPage() {
  const [entries, setEntries] = useState<AdminFeaturedVideo[]>([]);
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminFeaturedVideo | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AdminFeaturedVideo | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    /* Both, together. Unlike Home, a missing catalogue here is not survivable:
       the section column would render slugs and the picker would offer nothing,
       so a curator could see the gap and have no way to close it. */
    Promise.all([listFeaturedAdmin(), fetchHomeSections()])
      .then(([rows, catalogue]) => {
        setEntries(rows);
        setSections(catalogue);
      })
      .catch((err: unknown) => {
        setError(safeErrorMessage(err, 'Unable to load the featured list.'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sectionLabel = useCallback(
    (slug: string | null) => {
      if (!slug) return NO_SECTION_LABEL;
      /* A slug with no matching section means this build and the engine
         disagree about the catalogue. Showing the raw slug is right here — a
         curator needs to see the real stored value in order to fix it, which is
         the opposite of what Home does with the same situation. */
      return sections.find((section) => section.slug === slug)?.label ?? slug;
    },
    [sections],
  );

  /* Removing an entry is not the mirror image of adding one, which is why it
     asks first. Taking a video off Home also strands every user who saved it:
     `saved_content.content_id` holds this row's id with no foreign key behind
     it, so the records survive the delete, and re-adding the same job mints a
     new id that can never match them again. Nothing warns anyone, and nothing
     cleans them up.

     One click, next to Edit, in a button the same size and shape, is too little
     between a mis-aim and that. */
  const onRemove = useCallback(() => {
    if (!removing) return;
    const row = removing;
    setRemoving(null);
    setError(null);
    void deleteFeatured(row.id)
      .then(() => setEntries((current) => current.filter((entry) => entry.id !== row.id)))
      .catch((err: unknown) =>
        setError(safeErrorMessage(err, 'Unable to remove this entry.')),
      );
  }, [removing]);

  const columns = useMemo(
    () => buildColumns(sectionLabel, setEditing, setRemoving),
    [sectionLabel],
  );

  const uncategorised = entries.filter((entry) => !entry.category).length;

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-header">
        <h1>Featured</h1>
        <button type="button" className="button" onClick={() => setAdding(true)}>
          Feature a job
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : error && entries.length === 0 ? (
        /* With a retry, because this branch is the whole page: `entries` is
           empty, so there is nothing else on screen and no other control that
           would refetch. Without it a transient blip left F5 as the only way
           back, which is what BrowsePage already refuses to do. */
        <div>
          <ErrorBanner message={error} />
          <button type="button" className="button secondary" onClick={load}>
            Try again
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">Nothing is featured yet</div>
        </div>
      ) : (
        <>
          <p className="field-note">
            {entries.length} featured
            {uncategorised > 0 ? `, ${uncategorised} with no section` : ''}. An entry with no
            section appears under Recently Added and in no rail.
          </p>

          <DataTable
            columns={columns}
            data={entries}
            getRowId={(entry) => entry.id}
            enablePagination={true}
            pageSize={20}
          />

          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        </>
      )}

      {editing && (
        <EditFeaturedModal
          /* Keyed, so switching rows remounts rather than re-seeding. `form` is
             `useState(initial)`, which reads `initial` once — and `editing` can
             change while the modal is mounted, because AdminModal has no focus
             trap and leaves focus on the row button behind the overlay, so Tab
             and Enter reach another row's Edit. Unkeyed, the form kept row A's
             values while `entry.id` became row B, and Save wrote A's fields
             onto B. */
          key={editing.id}
          entry={editing}
          sections={sections}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEntries((current) =>
              current.map((entry) => (entry.id === updated.id ? updated : entry)),
            );
            setEditing(null);
          }}
        />
      )}

      {adding && (
        <AddFeaturedModal
          sections={sections}
          onClose={() => setAdding(false)}
          onCreated={(created) => {
            /* Placed by position, not appended. The table's first column is
               "Pos" and the server hands the list back in that order, so a new
               entry at position 0 sitting under position 12 reads as a value
               that failed to save. Ties go to the newcomer being last, matching
               the server's `position ASC, created_at DESC`. */
            setEntries((current) => {
              const next = [...current];
              const at = next.findIndex((entry) => entry.position > created.position);
              next.splice(at === -1 ? next.length : at, 0, created);
              return next;
            });
            setAdding(false);
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title="Remove from Home?"
        description={
          removing
            ? `“${removing.title}” comes off the Home page. Anyone who saved it keeps a card that
               can never be matched to this entry again — re-adding the same video creates a new
               one. The video itself is not deleted.`
            : undefined
        }
        confirmLabel="Remove"
        onConfirm={onRemove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
